import type { App, Ref } from 'vue'
import { inject, ref, toValue } from 'vue'
import type { InitQuery } from '@/graphql/api'

const pluginKey = 'app.plugin.menu'

export type MenuPlugin = {
    topBar: Readonly<Ref<InitQuery['topBar'] | undefined>>
    preHeader: Readonly<Ref<InitQuery['preHeader'] | undefined>>
    mainMenu: Readonly<Ref<InitQuery['mainMenu'] | undefined>>
    footer: Readonly<Ref<InitQuery['footer'] | undefined>>
    paymentIcons: Readonly<Ref<InitQuery['paymentIcons']>>
    socialNetworks: Readonly<Ref<InitQuery['socialNetworks']>>

    setTopBar(value: InitQuery['topBar']): void
    setPreHeader(value: InitQuery['preHeader']): void
    setMainMenu(menu: InitQuery['mainMenu']): void
    setFooter(menu: InitQuery['footer']): void
    setPaymentIcons(icons: InitQuery['paymentIcons']): void
    setSocialNetworks(networks: InitQuery['socialNetworks']): void
}

export function installMenuPlugin(app: App): MenuPlugin {
    const topBar = ref<InitQuery['topBar'] | undefined>(undefined)
    const preHeader = ref<InitQuery['preHeader'] | undefined>(undefined)
    const mainMenu = ref<InitQuery['mainMenu'] | undefined>(undefined)
    const footer = ref<InitQuery['footer'] | undefined>(undefined)
    const paymentIcons = ref<InitQuery['paymentIcons']>([])
    const socialNetworks = ref<InitQuery['socialNetworks']>([])

    const plugin: MenuPlugin = Object.freeze({
        topBar,
        preHeader,
        mainMenu,
        footer,
        paymentIcons,
        socialNetworks,
        setTopBar(value: InitQuery['topBar']) {
            topBar.value = value
        },
        setPreHeader(value: InitQuery['preHeader']) {
            preHeader.value = value
        },
        setMainMenu(menu: InitQuery['mainMenu']) {
            mainMenu.value = toValue(menu)
        },
        setFooter(menu: InitQuery['footer']) {
            footer.value = menu
        },
        setPaymentIcons(icons: InitQuery['paymentIcons']): void {
            paymentIcons.value = icons
        },
        setSocialNetworks(networks: InitQuery['socialNetworks']): void {
            socialNetworks.value = networks
        },
    })

    app.provide(pluginKey, plugin)

    return plugin
}

export function useMenus(): MenuPlugin {
    return inject(pluginKey) as MenuPlugin
}
